import { useQuery } from "@tanstack/react-query";
import { Ban, FileText, Paperclip, Pencil, Trash2, Undo2 } from "lucide-react";
import { useState } from "react";
import { useQuickAdd } from "@/components/AppShell";
import { PendingTag } from "@/components/PendingTag";
import { Button } from "@/components/ui/Button";
import { Field, Input } from "@/components/ui/Field";
import { Badge, ErrorState, errorMessage, Spinner, StatusText, useToast } from "@/components/ui/Feedback";
import { ConfirmDialog } from "@/components/ui/Modal";
import { Money } from "@/components/ui/Money";
import { api } from "@/lib/api";
import { formatDate, formatDateTime, TRANSACTION_TYPE_LABEL } from "@/lib/format";
import { useAccounts, useCategories, useLedgerMutation, useTransaction, useVoidTransaction } from "@/lib/queries";
import { transactionTitle } from "./TransactionRow";

interface AuditEntry {
  id: number;
  action: string;
  created_at: string;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
}

export function TransactionDetailView({ id, onClose }: { id: string; onClose: () => void }) {
  const { data: t, error, isPending, refetch } = useTransaction(id);
  const accounts = useAccounts();
  const categories = useCategories();
  const quickAdd = useQuickAdd();
  const toast = useToast();
  const [confirmVoid, setConfirmVoid] = useState(false);
  const [reason, setReason] = useState("");
  const audit = useQuery({
    queryKey: ["audit", id],
    queryFn: () => api.get<AuditEntry[]>("/audit-logs", { entity_id: id, limit: 20 }),
  });

  // Recorded on this device first, so voiding works with no network; the server confirms later.
  const voidTxn = useVoidTransaction();
  const upload = useLedgerMutation((file: File) => {
    const form = new FormData();
    form.append("file", file);
    return api.upload(`/transactions/${id}/attachments`, form);
  });
  const removeAttachment = useLedgerMutation((attId: string) => api.delete(`/attachments/${attId}`));

  if (isPending) return <Spinner />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;

  const nameOf = (p: { account_id: string | null; category_id: string | null }) =>
    p.account_id
      ? accounts.data?.find((a) => a.id === p.account_id)?.name ?? "Opening balances & adjustments"
      : categories.data?.find((c) => c.id === p.category_id)?.name ?? "Category";

  return (
    <div className="flex flex-col gap-5">
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge>{TRANSACTION_TYPE_LABEL[t.transaction_type]}</Badge>
          {t.is_voided && <Badge className="!text-bad">Voided</Badge>}
          {t.is_pending && <PendingTag />}
          {t.is_reconciled && <Badge>Reconciled</Badge>}
          {t.import_batch_id && <Badge>Imported</Badge>}
        </div>
        <p className="mt-2 text-lg font-semibold text-ink">{transactionTitle(t)}</p>
        <p className="mt-1 text-3xl font-semibold tracking-tight">
          <Money value={t.signed_amount} currency={t.currency} signed />
        </p>
        <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
          <dt className="text-ink-3">Date</dt><dd>{formatDate(t.transaction_date)}</dd>
          <dt className="text-ink-3">Account</dt>
          <dd>{t.account.name}{t.counter_account ? ` → ${t.counter_account.name}` : ""}</dd>
          {t.category && (<><dt className="text-ink-3">Category</dt><dd>{t.category.name}</dd></>)}
          {t.merchant && (<><dt className="text-ink-3">Merchant</dt><dd>{t.merchant.name}</dd></>)}
          {t.description && (<><dt className="text-ink-3">Description</dt><dd className="break-words">{t.description}</dd></>)}
          {t.reference && (<><dt className="text-ink-3">Reference</dt><dd className="font-mono text-xs leading-5">{t.reference}</dd></>)}
          {t.notes && (<><dt className="text-ink-3">Notes</dt><dd className="whitespace-pre-wrap">{t.notes}</dd></>)}
          {t.refunded_amount && Number(t.refunded_amount) > 0 && (
            <><dt className="text-ink-3">Refunded</dt><dd><Money value={t.refunded_amount} currency={t.currency} /></dd></>
          )}
          {t.is_voided && (
            <><dt className="text-ink-3">Voided</dt><dd>{formatDateTime(t.voided_at)}{t.void_reason ? ` — ${t.void_reason}` : ""}</dd></>
          )}
        </dl>
      </div>

      {t.is_reconciled && !t.is_voided && (
        <StatusText status="warning">
          This transaction is in a reconciled period. Editing it will mark that reconciliation as stale — voiding is usually better.
        </StatusText>
      )}

      {!t.is_voided && (
        <div className="flex flex-wrap gap-2">
          <Button size="sm" icon={<Pencil className="size-4" />} onClick={() => { onClose(); quickAdd({ transaction: t }); }}>
            Edit
          </Button>
          {t.transaction_type === "expense" && (
            <Button size="sm" icon={<Undo2 className="size-4" />}
                    onClick={() => { onClose(); quickAdd({ defaults: { transaction_type: "refund", original: t } }); }}>
              Record refund
            </Button>
          )}
          <Button size="sm" variant="danger" icon={<Ban className="size-4" />} onClick={() => setConfirmVoid(true)}>
            Void
          </Button>
        </div>
      )}

      {t.postings.length > 0 && (
      <section>
        <h3 className="mb-2 text-sm font-semibold">Postings (double entry)</h3>
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-ink-3">
            <tr><th className="py-1 font-medium">Ledger</th><th className="py-1 text-right font-medium">Debit</th><th className="py-1 text-right font-medium">Credit</th></tr>
          </thead>
          <tbody>
            {t.postings.map((p) => (
              <tr key={p.id} className="border-t border-line">
                <td className="py-1.5">{nameOf(p)}<span className="ml-1.5 text-xs text-ink-3">{p.account_id ? "account" : "category"}</span></td>
                <td className="py-1.5 text-right">{p.direction === "debit" && <Money value={p.amount} currency={p.currency} />}</td>
                <td className="py-1.5 text-right">{p.direction === "credit" && <Money value={p.amount} currency={p.currency} />}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
      )}

      <section>
        <h3 className="mb-2 flex items-center gap-1.5 text-sm font-semibold"><Paperclip className="size-4" aria-hidden />Receipts & attachments</h3>
        {t.attachments.length > 0 && (
          <ul className="mb-2 flex flex-col gap-1">
            {t.attachments.map((a) => (
              <li key={a.id} className="flex items-center justify-between gap-2 rounded-lg border border-border px-3 py-2 text-sm">
                <a href={a.download_url} className="flex min-w-0 items-center gap-2 text-accent hover:underline">
                  <FileText className="size-4 shrink-0" aria-hidden />
                  <span className="truncate">{a.original_filename}</span>
                </a>
                <button type="button" aria-label={`Delete ${a.original_filename}`}
                        className="rounded p-1 text-ink-3 hover:bg-surface-2 hover:text-bad"
                        onClick={() => removeAttachment.mutate(a.id, { onError: (e) => toast(errorMessage(e), "error") })}>
                  <Trash2 className="size-4" aria-hidden />
                </button>
              </li>
            ))}
          </ul>
        )}
        {t.is_pending ? (
          <p className="text-sm text-ink-3">Receipts can be attached once this transaction has synced.</p>
        ) : (
        <label className="inline-flex cursor-pointer items-center gap-2 text-sm font-medium text-accent hover:underline">
          <input
            type="file"
            className="sr-only"
            accept="application/pdf,image/png,image/jpeg,image/webp"
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = "";
              if (file) upload.mutate(file, {
                onSuccess: () => toast("Attachment added"),
                onError: (err) => toast(errorMessage(err), "error"),
              });
            }}
          />
          {upload.isPending ? "Uploading…" : "Attach a receipt (PDF, PNG, JPEG, WebP · max 5 MB)"}
        </label>
        )}
      </section>

      <section>
        <h3 className="mb-2 text-sm font-semibold">History</h3>
        <ol className="flex flex-col gap-1.5 text-sm">
          {(audit.data ?? []).map((a) => (
            <li key={a.id} className="flex justify-between gap-3">
              <span className="text-ink-2">{a.action.replace("transaction.", "").replace("_", " ")}
                {a.after && a.action === "transaction.updated" && (
                  <span className="text-ink-3"> · {Object.keys(a.after).filter((k) => k !== "postings").join(", ")}</span>
                )}
              </span>
              <span className="shrink-0 text-xs text-ink-3">{formatDateTime(a.created_at)}</span>
            </li>
          ))}
        </ol>
      </section>

      <ConfirmDialog
        open={confirmVoid}
        title="Void this transaction?"
        message="It stays in your history but no longer counts toward balances, budgets or reports. This cannot be undone."
        confirmLabel="Void transaction"
        busy={voidTxn.isPending}
        onClose={() => setConfirmVoid(false)}
        onConfirm={() => voidTxn.mutate({ id: t.id, reason: reason.trim() || null }, {
          onSuccess: (outcome) => {
            setConfirmVoid(false);
            toast(outcome.queued ? "Voided on this device. It will sync automatically." : "Transaction voided");
            void refetch();
            void audit.refetch();
          },
          onError: (e) => toast(errorMessage(e), "error"),
        })}
      >
        <Field label="Reason (optional)" className="mt-3">
          {({ id: fid }) => <Input id={fid} value={reason} maxLength={200} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Entered twice" />}
        </Field>
      </ConfirmDialog>
    </div>
  );
}
